import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Collapse, Flex, Heading, HStack, Icon, Stack, Text, VStack } from "@chakra-ui/react";
import { FiCheck, FiChevronDown, FiChevronUp, FiArrowRight } from "react-icons/fi";
import { motion, useReducedMotion } from "framer-motion";
import { alpha, useGovTheme } from "./useGovTheme";
import { GovSurface } from "./GovSurface";
import { GovProgressRing } from "./GovProgressRing";
import { useCelebrate } from "./GovCelebrate";
import { GovTip } from "./GovTip";
import { blurClick } from "./blurClick";

interface OrgLike { id: string; purpose?: string | null; rulesMarkdown?: string | null; defaultQuorumPercent?: number | null; status?: string | null }

interface Step { key: string; title: string; why: string; done: boolean; cta?: { label: string; to?: string; onClick?: () => void } }

const storageGet = (k: string) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const storageSet = (k: string, v: string) => { try { window.localStorage.setItem(k, v); } catch { /* private window: fine */ } };

/**
 * Getting a group going, as six real steps (2026-09-12). Every tick is computed from what the group
 * actually holds — nothing is ticked for effort. When all six are done it says so once, with a small
 * celebration, and folds away. People who can't manage the group see the progress but no buttons.
 */
export function GovSetupChecklist({ org, activeMemberCount, proposalCount, canManage, onOpenProposals }: {
  org: OrgLike; activeMemberCount: number; proposalCount: number; canManage: boolean; onOpenProposals?: () => void;
}) {
  const t = useGovTheme();
  const reduce = useReducedMotion();
  const { celebrate, node } = useCelebrate();
  const settings = `/governance/${org.id}/settings`;
  const steps: Step[] = [
    { key: "purpose", title: "Say why it exists", why: "One line at the top of every page and the public page.", done: Boolean(org.purpose?.trim()), cta: { label: "Write it", to: settings } },
    { key: "rules", title: "Write the rules", why: "How decisions are made — members read this before they vote.", done: Boolean(org.rulesMarkdown?.trim()), cta: { label: "Write them", to: settings } },
    { key: "members", title: "Bring people in", why: "A vote needs voters: at least two members.", done: activeMemberCount >= 2, cta: { label: "Invite", to: `/governance/${org.id}/invites` } },
    { key: "quorum", title: "Set a quorum", why: "How many must vote for a decision to count.", done: (org.defaultQuorumPercent ?? 0) > 0, cta: { label: "Set it", to: settings } },
    { key: "active", title: "Go live", why: "A draft group can’t hold votes yet.", done: org.status === "ACTIVE", cta: { label: "Open it", to: settings } },
    { key: "motion", title: "Put the first motion", why: "The first decision the group makes together.", done: proposalCount >= 1, cta: { label: "Propose", onClick: onOpenProposals } },
  ];
  const done = steps.filter((s) => s.done).length;
  const complete = done === steps.length;
  const celebratedKey = `gov-setup-celebrated:${org.id}`;
  const foldKey = `gov-setup-folded:${org.id}`;
  const [open, setOpen] = React.useState(() => storageGet(foldKey) !== "1");
  React.useEffect(() => {
    if (complete && storageGet(celebratedKey) !== "1") { storageSet(celebratedKey, "1"); celebrate(); setOpen(false); }
  }, [complete, celebratedKey, celebrate]);
  const next = steps.find((s) => !s.done);
  const toggle = () => { setOpen((o) => { storageSet(foldKey, o ? "1" : "0"); return !o; }); };

  return (
    <GovSurface borderColor={complete ? alpha(t.success, 0.35) : t.border}>
      {node}
      <Stack direction={{ base: "column", sm: "row" }} spacing={4} align={{ sm: "center" }} justify="space-between">
        <HStack spacing={4} align="center">
          <GovProgressRing value={done} max={steps.length} label={`${done}/${steps.length}`} size={64} tone={complete ? t.success : undefined} />
          <Box>
            <Heading size="sm" color={t.text}>{complete ? "This group is set up and running" : "Get this group going"}</Heading>
            <Text fontSize="md" color={t.muted}>
              {complete ? "Every step is done. Decisions from here on build the group’s record." : next ? <>Next: <b>{next.title.toLowerCase()}</b> — {next.why}</> : null}
            </Text>
          </Box>
        </HStack>
        <GovTip label={open ? "Fold the setup steps away. Nothing changes." : "Show the setup steps."}>
          <Button size="md" variant="ghost" rightIcon={<Icon as={open ? FiChevronUp : FiChevronDown} />} onClick={blurClick(toggle)} minH="44px" alignSelf={{ base: "flex-start", sm: "center" }}>
            {open ? "Hide steps" : "Show steps"}
          </Button>
        </GovTip>
      </Stack>
      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={2} mt={4}>
          {steps.map((s, i) => (
            <HStack key={s.key} spacing={3} p={3} borderRadius="xl" bg={s.done ? alpha(t.success, 0.07) : t.raised} align="center">
              <motion.div initial={reduce ? false : { scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: reduce ? 0 : i * 0.05, type: "spring", stiffness: 500, damping: 28 }}>
                <Flex w={8} h={8} borderRadius="full" align="center" justify="center" flexShrink={0}
                  bg={s.done ? t.success : "transparent"} borderWidth={s.done ? 0 : "2px"} borderColor={t.borderStrong}>
                  {s.done ? <Icon as={FiCheck} color="white" boxSize={4} /> : <Text fontSize="sm" color={t.faint} fontWeight="bold">{i + 1}</Text>}
                </Flex>
              </motion.div>
              <Box flex={1} minW={0}>
                <Text fontSize="md" fontWeight="semibold" color={t.text} textDecoration={s.done ? "line-through" : undefined} opacity={s.done ? 0.75 : 1}>{s.title}</Text>
                {!s.done && <Text fontSize="sm" color={t.muted}>{s.why}</Text>}
              </Box>
              {!s.done && canManage && s.cta && (s.cta.to || s.cta.onClick) && (
                <GovTip label={`${s.cta.label}: ${s.why}`}>
                  {s.cta.to
                    ? /* blur-exempt: navigates away */ <Button as={RouterLink} to={s.cta.to} size="md" variant="outline" rightIcon={<Icon as={FiArrowRight} />} minH="44px" flexShrink={0}>{s.cta.label}</Button>
                    : <Button size="md" variant="outline" rightIcon={<Icon as={FiArrowRight} />} minH="44px" flexShrink={0} onClick={blurClick(() => s.cta!.onClick!())}>{s.cta.label}</Button>}
                </GovTip>
              )}
            </HStack>
          ))}
        </VStack>
      </Collapse>
    </GovSurface>
  );
}
